import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Settings, Wallet, Loader2, Save, ArrowLeft, DollarSign, User, AlertTriangle, Tag, Plus, Pencil, Trash2 } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

export default function CajaChicaConfigPage() {
  const [config, setConfig] = useState({
    monto_autorizado: 50000,
    monto_minimo_reposicion: 10000,
    responsable_id: "",
    descripcion: ""
  });
  const [users, setUsers] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showCategoriaDialog, setShowCategoriaDialog] = useState(false);
  const [editingCategoria, setEditingCategoria] = useState(null);
  const [categoriaForm, setCategoriaForm] = useState({ value: "", label: "" });
  const [savingCategoria, setSavingCategoria] = useState(false);
  const { getAuthHeaders, user } = useAuth();
  const navigate = useNavigate();
  
  const isAdmin = user?.role?.toLowerCase() === "administrador" || user?.role?.toLowerCase() === "admin";

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [configRes, usersRes, categoriasRes] = await Promise.all([
        axios.get(`${API}/caja-chica/config`, getAuthHeaders()),
        axios.get(`${API}/users`, getAuthHeaders()),
        axios.get(`${API}/caja-chica/categorias`, getAuthHeaders())
      ]);
      
      setConfig({
        monto_autorizado: configRes.data.monto_autorizado || 50000,
        monto_minimo_reposicion: configRes.data.monto_minimo_reposicion || 10000,
        responsable_id: configRes.data.responsable_id || "",
        descripcion: configRes.data.descripcion || ""
      });
      setUsers(usersRes.data || []);
      setCategorias(categoriasRes.data || []);
    } catch (err) {
      toast.error("Error al cargar configuración");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    if (!isAdmin) {
      toast.error("No tiene permisos para acceder a esta página");
      navigate("/caja-chica");
      return;
    }
    fetchData();
  }, [fetchData, isAdmin, navigate]);

  const handleSave = async () => {
    if (config.monto_autorizado <= 0) {
      toast.error("El monto autorizado debe ser mayor a 0");
      return;
    }
    if (config.monto_minimo_reposicion >= config.monto_autorizado) {
      toast.error("El monto mínimo de reposición debe ser menor al monto autorizado");
      return;
    }
    
    setSaving(true);
    try {
      await axios.put(`${API}/caja-chica/config`, config, getAuthHeaders());
      toast.success("Configuración guardada exitosamente");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const formatCurrency = (val) => `RD$ ${(val || 0).toLocaleString('es-DO', { minimumFractionDigits: 2 })}`;

  // Category management functions
  const openCategoriaDialog = (categoria = null) => {
    if (categoria) {
      setEditingCategoria(categoria);
      setCategoriaForm({ value: categoria.value, label: categoria.label });
    } else {
      setEditingCategoria(null);
      setCategoriaForm({ value: "", label: "" });
    }
    setShowCategoriaDialog(true);
  };

  const handleSaveCategoria = async () => {
    if (!categoriaForm.value || !categoriaForm.label) {
      toast.error("Complete todos los campos");
      return;
    }
    
    setSavingCategoria(true);
    try {
      if (editingCategoria) {
        await axios.put(`${API}/caja-chica/categorias/${editingCategoria.id}`, categoriaForm, getAuthHeaders());
        toast.success("Categoría actualizada");
      } else {
        await axios.post(`${API}/caja-chica/categorias`, categoriaForm, getAuthHeaders());
        toast.success("Categoría creada");
      }
      setShowCategoriaDialog(false);
      setCategoriaForm({ value: "", label: "" });
      setEditingCategoria(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar categoría");
    } finally {
      setSavingCategoria(false);
    }
  };

  const handleDeleteCategoria = async (categoria) => {
    if (!confirm(`¿Eliminar la categoría "${categoria.label}"?`)) return;
    
    try {
      await axios.delete(`${API}/caja-chica/categorias/${categoria.id}`, getAuthHeaders());
      toast.success("Categoría eliminada");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden" data-testid="caja-chica-config-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Link to="/caja-chica">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold dark:text-white flex items-center gap-3">
                <Settings className="w-8 h-8 text-[#0C314C]" />
                Configuración de Caja Chica
              </h1>
              <p className="text-slate-500">Configure los parámetros del fondo de caja chica</p>
            </div>
          </div>
          <Button 
            onClick={handleSave} 
            disabled={saving} 
            className="bg-[#0C314C] hover:bg-[#2a4d7a] text-white"
            data-testid="save-config-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Guardar Cambios
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Monto Autorizado */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-green-600" />
                  Monto Autorizado
                </CardTitle>
                <CardDescription>
                  El monto máximo total que puede tener la caja chica
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="monto_autorizado">Monto Total (RD$)</Label>
                    <Input
                      id="monto_autorizado"
                      type="number"
                      min="0"
                      step="1000"
                      value={config.monto_autorizado}
                      onChange={(e) => setConfig({ ...config, monto_autorizado: parseFloat(e.target.value) || 0 })}
                      className="text-lg font-semibold"
                      data-testid="monto-autorizado-input"
                    />
                    <p className="text-sm text-slate-500 mt-1">
                      Valor actual: {formatCurrency(config.monto_autorizado)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Monto Mínimo Reposición */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  Alerta de Reposición
                </CardTitle>
                <CardDescription>
                  Cuando el saldo baje de este monto, se mostrará alerta
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="monto_minimo">Monto Mínimo (RD$)</Label>
                    <Input
                      id="monto_minimo"
                      type="number"
                      min="0"
                      step="1000"
                      value={config.monto_minimo_reposicion}
                      onChange={(e) => setConfig({ ...config, monto_minimo_reposicion: parseFloat(e.target.value) || 0 })}
                      className="text-lg font-semibold"
                      data-testid="monto-minimo-input"
                    />
                    <p className="text-sm text-slate-500 mt-1">
                      Valor actual: {formatCurrency(config.monto_minimo_reposicion)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Responsable */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-600" />
                  Responsable
                </CardTitle>
                <CardDescription>
                  Usuario encargado del manejo de la caja chica
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="responsable">Seleccionar Responsable</Label>
                    <Select 
                      value={config.responsable_id || "none"} 
                      onValueChange={(val) => setConfig({ ...config, responsable_id: val === "none" ? "" : val })}
                    >
                      <SelectTrigger data-testid="responsable-select">
                        <SelectValue placeholder="Seleccione un usuario" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Sin asignar</SelectItem>
                        {users.filter(u => u.activo !== false).map((u) => (
                          <SelectItem key={u.id} value={u.id}>
                            {u.name} ({u.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Descripción */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wallet className="w-5 h-5 text-purple-600" />
                  Descripción
                </CardTitle>
                <CardDescription>
                  Notas o descripción sobre el uso de la caja chica
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="descripcion">Notas</Label>
                    <Textarea
                      id="descripcion"
                      value={config.descripcion}
                      onChange={(e) => setConfig({ ...config, descripcion: e.target.value })}
                      placeholder="Ej: Fondo para gastos menores del departamento administrativo..."
                      rows={4}
                      data-testid="descripcion-textarea"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Categorías de Gastos */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Tag className="w-5 h-5 text-orange-600" />
                      Categorías de Gastos
                    </CardTitle>
                    <CardDescription>
                      Administre las categorías disponibles para clasificar los gastos
                    </CardDescription>
                  </div>
                  <Button 
                    onClick={() => openCategoriaDialog()}
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                    data-testid="add-categoria-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nueva Categoría
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {categorias.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <Tag className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    <p>No hay categorías configuradas</p>
                    <p className="text-sm">Agregue categorías para clasificar los gastos</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {categorias.map((cat) => (
                      <div 
                        key={cat.id} 
                        className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
                      >
                        <div>
                          <p className="font-medium text-slate-800 dark:text-slate-200">{cat.label}</p>
                          <p className="text-xs text-slate-500">{cat.value}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => openCategoriaDialog(cat)}
                            data-testid={`edit-cat-${cat.value}`}
                          >
                            <Pencil className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDeleteCategoria(cat)}
                            data-testid={`delete-cat-${cat.value}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Info Card */}
        <Card className="mt-6 border-blue-200 bg-blue-50">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Settings className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-medium text-blue-900">Información</h4>
                <p className="text-sm text-blue-700 mt-1">
                  Los cambios en la configuración afectarán inmediatamente el cálculo de saldos y alertas de reposición.
                  El historial de transacciones no se verá afectado.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Categoria Dialog */}
        <Dialog open={showCategoriaDialog} onOpenChange={setShowCategoriaDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Tag className="w-5 h-5 text-orange-600" />
                {editingCategoria ? "Editar Categoría" : "Nueva Categoría"}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Nombre de la Categoría *</Label>
                <Input 
                  value={categoriaForm.label} 
                  onChange={(e) => setCategoriaForm({ 
                    ...categoriaForm, 
                    label: e.target.value,
                    value: editingCategoria ? categoriaForm.value : e.target.value.toLowerCase().replace(/\s+/g, '_').normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                  })}
                  placeholder="Ej: Materiales de Oficina"
                  data-testid="categoria-label-input"
                />
              </div>
              <div>
                <Label>Código Interno</Label>
                <Input 
                  value={categoriaForm.value} 
                  onChange={(e) => setCategoriaForm({ ...categoriaForm, value: e.target.value })}
                  placeholder="Ej: materiales_oficina"
                  disabled={!!editingCategoria}
                  className={editingCategoria ? "bg-slate-100" : ""}
                  data-testid="categoria-value-input"
                />
                <p className="text-xs text-slate-500 mt-1">
                  {editingCategoria ? "El código no se puede modificar" : "Se genera automáticamente"}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCategoriaDialog(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleSaveCategoria} 
                disabled={savingCategoria}
                className="bg-orange-600 hover:bg-orange-700 text-white"
                data-testid="save-categoria-btn"
              >
                {savingCategoria && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingCategoria ? "Actualizar" : "Crear"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
